import { format } from 'date-fns'

export const getNow = () => {
  return new Date().getTime()
}

export const toReadableDateTime = (date: number, isRequireTime = true) =>
  new Intl.DateTimeFormat(
    'en-US',
    isRequireTime
      ? {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }
      : {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        },
  ).format(new Date(date))

export const toLocalISODateTime = (utcTimestamp?: number): string => {
  if (!utcTimestamp) return ''
  const date = new Date(utcTimestamp)
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

export const toISODate = (date: number) => new Date(date).toISOString().split('T')[0]

export const formatDateRange = (startDate: Date, endDate: Date): string => {
  const monthFormatter = new Intl.DateTimeFormat('en', { month: 'short' })
  const dayFormatter = new Intl.DateTimeFormat('en', { day: 'numeric' })

  const startMonth = monthFormatter.format(startDate)
  const startDay = dayFormatter.format(startDate)
  const endMonth = monthFormatter.format(endDate)
  const endDay = dayFormatter.format(endDate)

  switch (true) {
    case startMonth === endMonth && startDay === endDay:
      return `${startMonth} ${startDay}`
    case startMonth === endMonth:
      return `${startMonth} ${startDay} - ${endDay}`
    default:
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
  }
}

export const getDateTimeFromString = (
  dateStr: string,
  timeStr: string,
): {
  year: number
  month: number
  day: number
  hours: number
  minutes: number
  date: Date
} => {
  const [year, month, day] = dateStr.split('/').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)

  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hours === undefined ||
    minutes === undefined ||
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    throw new Error('Invalid date or time')
  }
  return {
    year,
    month,
    day,
    hours,
    minutes,
    date: new Date(year, month - 1, day, hours, minutes),
  }
}

export const validateDate = (dateStr: string, timeStr: string) => {
  const { year, month, day, hours, minutes, date } = getDateTimeFromString(dateStr, timeStr)

  switch (true) {
    case !year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes):
    case Number.isNaN(date.getTime()):
    case date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day:
    case hours < 0 || hours > 23 || minutes < 0 || minutes > 59:
      throw new Error('Invalid date')
    default:
  }
}

export const toReadableDurationTime = (millis: number): string => {
  if (millis === 0) {
    return '0 m'
  }

  if (millis < 60 * 1000) {
    return '1 m'
  }

  const totalMinutes = Math.floor(millis / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  const hourUnit = hours > 1 ? 'h' : 'h'
  const minuteUnit = minutes > 1 ? 'm' : 'm'

  if (hours > 0) {
    return `${hours} ${hourUnit} ${minutes} ${minuteUnit}`
  }
  if (hours === 0 && minutes === 0) {
    return ''
  }
  return `${minutes} ${minuteUnit}`
}

export const toReadableDate = (dateInMillis: number) => {
  if (!dateInMillis) return ''
  const date = new Date(dateInMillis)
  return format(date, 'yyyy MMM dd')
}

export const toDateString = (dateInMillis: number) => {
  if (!dateInMillis) return ''
  const date = new Date(dateInMillis)
  return format(date, 'MMM d')
}

export const millisecondsToHours = (seconds: number) => {
  return Math.floor(seconds / 1000 / 60 / 60)
}

export const toRelativeTime = (timestamp: number): string => {
  const now = Date.now()
  const diff = now - timestamp

  if (diff < ONE_MINUTE) {
    return 'just now'
  }

  if (diff < ONE_HOUR) {
    const minutes = Math.floor(diff / ONE_MINUTE)
    return `${minutes}m ago`
  }

  if (diff < ONE_DAY) {
    const hours = Math.floor(diff / ONE_HOUR)
    return `${hours}h ago`
  }

  if (diff < ONE_WEEK) {
    const days = Math.floor(diff / ONE_DAY)
    return `${days}d ago`
  }

  return toReadableDate(timestamp)
}

export const getTimeZoneOffset = (timeZone: string): string => {
  const now = new Date()

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now)

  const hourPart = parts.find((p) => p.type === 'hour')
  const minutePart = parts.find((p) => p.type === 'minute')

  if (!hourPart || !minutePart) {
    return ''
  }

  const localHour = Number.parseInt(hourPart.value, 10)
  const localMinute = Number.parseInt(minutePart.value, 10)

  const utcHour = now.getUTCHours()
  const utcMinute = now.getUTCMinutes()

  const localTotal = localHour * 60 + localMinute
  const utcTotal = utcHour * 60 + utcMinute

  let offsetMinutes = localTotal - utcTotal
  // Adjust for day wrap
  if (offsetMinutes < -720) offsetMinutes += 1440
  if (offsetMinutes > 720) offsetMinutes -= 1440

  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absMinutes = Math.abs(offsetMinutes)
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0')
  const minutes = String(absMinutes % 60).padStart(2, '0')

  return `GMT ${sign}${hours}:${minutes}`
}

export const ONE_WEEK = 7 * 24 * 60 * 60 * 1000
export const ONE_DAY = 24 * 60 * 60 * 1000
export const ONE_HOUR = 60 * 60 * 1000
export const ONE_SECOND = 1000
export const ONE_MINUTE = 60 * ONE_SECOND
